import { getFormFieldValidations, isEmailValid } from '@avalara/bat-apps-shared/lib/form-validation';

import { requiredFormFields } from '../reducers/helpers/forms-constants';

import type { SharedSignupState } from '@avalara/types/reducer-states';

export const isFieldMissing = (state: SharedSignupState) => (field: string) =>
  state.signupForm.country !== 'US' && state.signupForm.country !== 'CA' && field === 'postalCode'
    ? false
    : !state.signupForm[field as keyof typeof state.signupForm]?.trim() &&
      (requiredFormFields.includes(field) || state.additionalRequiredFields.includes(field));

export const isFieldInvalid =
  ({ signupForm, useEcmCompanyLength }: SharedSignupState) =>
  (field: string) => {
    if (field === 'street1' && [signupForm.street1, signupForm.street2].filter(Boolean).join(' ').length > 50)
      return true;

    // NOTE (Sasha): Given the non-standard formats that other countries set for
    // postalCode and the vast number of regions for other countries, skip validation
    // if not US or Canada
    if (signupForm.country !== 'US' && signupForm.country !== 'CA' && (field === 'postalCode' || field === 'state'))
      return false;

    if (field === 'email') return !isEmailValid(signupForm.email);

    const fieldValidations = getFormFieldValidations(signupForm.country, useEcmCompanyLength);
    type FieldKey = keyof typeof fieldValidations;
    type SignupFormKey = keyof typeof signupForm;

    return Boolean(
      fieldValidations[field as FieldKey] &&
        !fieldValidations[field as FieldKey].test(signupForm[field as SignupFormKey] || ''),
    );
  };

const isFieldMissingOrInvalid = (state: SharedSignupState) => (field: string) => {
  const normalValidation = isFieldMissing(state)(field) || isFieldInvalid(state)(field);
  if (field === 'email') {
    return normalValidation || !state.emailIsVerified;
  }
  if (field === 'password') {
    return isFieldInvalid(state)(field);
  }
  return normalValidation;
};

// Returns array of all invalid fields (missing or failing a regex validation)
export const getInvalidFields = (state: SharedSignupState): Array<string> =>
  Object.keys(state.signupForm).filter(isFieldMissingOrInvalid(state));

// Returns true if field is missing or invalid, *unless we've yet to visit it*
export const isFieldVisitedAndInvalid = (state: SharedSignupState, field: string) => {
  if (!state.fieldsClearedForValidation[field]) return false;

  return isFieldMissingOrInvalid(state)(field);
};

const areSomeFormFieldsNotCompleted =
  (fields: Array<string>) =>
  (state: SharedSignupState): boolean =>
    fields.some(isFieldMissingOrInvalid(state));

// Same function as isFormIncomplete, but specific to page 1 of avatax signup
export const isFormOneIncomplete = areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'phone']);

// Same function as isFormIncomplete, but specific to page 1 of offer signup
export const isOfferFormOneIncomplete = (state: SharedSignupState, isPasswordRequired?: boolean): boolean =>
  isPasswordRequired
    ? areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'companyName', 'password'])(state)
    : areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'companyName'])(state);

export const esbFormOneFields = ['companyName', 'country', 'street1', 'city', 'state', 'postalCode', 'phone'];
// Same function as isFormIncomplete, but specific to ESB signup
export const isEsbFormOneIncomplete = areSomeFormFieldsNotCompleted(esbFormOneFields);

export const isEsbFormTwoIncomplete = areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'password']);

// Helper to track if entire form (regardless of how many pages its broken into) is complete
export const isFormIncomplete = (state: SharedSignupState): boolean => getInvalidFields(state).length > 0;
